import { useEffect, useState } from "react";
import { useHashScroll } from "@/hooks/useHashScroll";
import { ArrowRight, ShoppingCart, Books, PencilLine, Lifebuoy, Gift, GraduationCap } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { SiteNav } from "@/components/home/SiteNav";
import { SiteFooter } from "@/components/home/SiteFooter";
import { ShowDontTell } from "@/components/home/ShowDontTell";
import { AmazonBooks } from "@/components/home/AmazonBooks";
import { FreeTools } from "@/components/home/FreeTools";
import { CourseComingSoon } from "@/components/home/CourseComingSoon";
import { NewsletterSection } from "@/components/home/NewsletterSection";
import { TPT_PRODUCTS, CATEGORIES } from "@/data/tptProducts";
import { TPT_STORE_URL } from "@/data/storeLinks";

const JUMP = [
  { id: "tpt", label: "Printable Resources on TpT", icon: ShoppingCart },
  { id: "amazon", label: "Paperback Workbooks on Amazon", icon: Books },
  { id: "show-dont-tell", label: "Show, Don't Tell", icon: PencilLine },
  { id: "support-guide", label: "Support Guide", icon: Lifebuoy },
  { id: "free-tools", label: "Free Tools", icon: Gift },
  { id: "course", label: "Course (Coming Soon)", icon: GraduationCap },
];

const TIPS = [
  { title: "Introduce one word at a time", body: "Say it, show it, and use it in a sentence about your child's day before any writing begins." },
  { title: "Use tracing as a bridge", body: "Tracing builds muscle memory and confidence. Fade it once the word feels familiar — it's a step, not the goal." },
  { title: "Choose → complete → write", body: "Move from picking the right answer, to finishing a sentence, to writing independently. Celebrate each stage." },
  { title: "Break lessons into small steps", body: "One page, one skill. Short, predictable routines beat long sessions every time." },
  { title: "Supporting SPED learners", body: "Keep layouts consistent, read directions aloud, allow extra processing time, and start with the supported-practice pages." },
  { title: "Supporting ESL/ELL learners", body: "Pair each word with a gesture or picture, model full sentences, and invite the child to say it in their home language too." },
  { title: "Spread a workbook across several days", body: "A few pages a day keeps attention fresh. Revisit earlier words briefly before starting a new one." },
];

const TptCatalog = () => {
  const [cat, setCat] = useState("all");
  const list = cat === "all" ? TPT_PRODUCTS : TPT_PRODUCTS.filter((p) => p.category === cat);
  return (
    <section id="tpt" className="py-12 sm:py-16 px-4 scroll-mt-32" aria-labelledby="tpt-title">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
          <div className="max-w-2xl">
            <span className="font-nunito font-bold text-xs uppercase tracking-[0.2em] text-[#7358b3]">Digital · print at home or school</span>
            <h2 id="tpt-title" className="font-nunito font-extrabold tracking-tight text-3xl sm:text-4xl text-[#17284b] mt-2">Printable Resources on TpT</h2>
            <p className="font-nunito text-base text-[#596882] mt-2">
              {TPT_PRODUCTS.length} digital workbooks, bundles, teacher's guides, answer keys, supported-practice packets, and no-prep activities.
            </p>
          </div>
          <a href={TPT_STORE_URL} target="_blank" rel="noopener noreferrer" data-testid="tpt-store-btn">
            <Button className="bg-[#0f285f] hover:bg-[#15367a] text-white font-nunito font-bold rounded-full px-6 border-0">
              Visit the TpT store <ArrowRight size={16} weight="bold" className="ml-2" />
            </Button>
          </a>
        </div>
        <div className="flex flex-wrap gap-2 mb-6" role="tablist" aria-label="Product categories">
          {CATEGORIES.map((c) => {
            const active = cat === c.id;
            const count = c.id === "all" ? TPT_PRODUCTS.length : TPT_PRODUCTS.filter((p) => p.category === c.id).length;
            return (
              <button key={c.id} role="tab" aria-selected={active} onClick={() => setCat(c.id)} data-testid={`product-category-${c.id}`}
                className={`px-4 py-2 rounded-full border font-nunito font-bold text-sm transition-colors ${active ? "bg-[#0f285f] text-white border-[#0f285f]" : "bg-white text-[#17284b] border-[#d4dff0] hover:border-[#7358b3]"}`}>
                {c.label} <span className="ml-1 text-xs opacity-70">{count}</span>
              </button>
            );
          })}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4" data-testid="tpt-grid">
          {list.map((p) => (
            <a key={p.id} href={p.link} target="_blank" rel="noopener noreferrer" data-testid={`buy-btn-${p.id}`}
              className="group bg-white border border-[#d4dff0] rounded-2xl overflow-hidden hover:shadow-lg hover:border-[#7358b3] transition-all flex flex-col">
              <div className="relative bg-white p-3">
                {p.free && <span className="absolute top-2 right-2 bg-emerald-500 text-white text-[11px] font-nunito font-bold px-2 py-0.5 rounded-full">FREE</span>}
                <img src={p.image} alt={`${p.title} worksheet cover`} className="w-full aspect-[4/5] object-contain" loading="lazy" />
              </div>
              <div className="p-3 border-t border-[#d4dff0] flex-1 flex flex-col gap-1">
                <span className="font-nunito text-[11px] font-bold text-[#596882] uppercase tracking-wide">{p.grades}</span>
                <h3 className="font-nunito font-bold text-sm text-[#17284b] leading-snug line-clamp-2">{p.title}</h3>
                <span className={`font-nunito font-extrabold text-sm mt-auto ${p.free ? "text-emerald-600" : "text-[#ff8518]"}`}>{p.price}</span>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
};

export default function ResourcesPage() {
  useHashScroll();
  useEffect(() => {
    document.title = "Resources for Parents, Teachers & Learning Support Professionals | Embrace the Magic of Words";
  }, []);

  return (
    <div className="min-h-screen bg-[#fffdf7]" data-testid="resources-page">
      <SiteNav />
      <header className="bg-[#0f285f] text-white px-4 py-12 sm:py-16">
        <div className="max-w-7xl mx-auto">
          <span className="font-nunito font-bold text-xs uppercase tracking-[0.2em] text-[#ffb066]">For grown-ups</span>
          <h1 className="font-nunito font-extrabold tracking-tight text-3xl sm:text-4xl lg:text-5xl mt-2 leading-tight max-w-4xl text-balance" data-testid="resources-title">
            Resources for Parents, Teachers &amp; Learning Support Professionals
          </h1>
          <p className="font-nunito text-base sm:text-lg text-white/80 mt-4 max-w-2xl">
            Explore structured printables, paperback workbooks, free learning tools, and step-by-step support designed to help diverse learners build vocabulary and communicate with confidence.
          </p>
          <nav className="flex flex-wrap gap-2 mt-7" aria-label="Jump to section">
            {JUMP.map((j) => (
              <a key={j.id} href={`#${j.id}`} data-testid={`resources-jump-${j.id}`}
                className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 border border-white/25 rounded-full px-4 py-2 font-nunito font-bold text-sm transition-colors">
                <j.icon size={16} weight="bold" className="text-[#ffb066]" /> {j.label}
              </a>
            ))}
          </nav>
        </div>
      </header>
      <main>
        <TptCatalog />
        <AmazonBooks />
        <ShowDontTell />
        <section id="support-guide" className="py-12 sm:py-16 px-4 scroll-mt-32 bg-white border-t border-[#d4dff0]" aria-labelledby="support-guide-title">
          <div className="max-w-4xl mx-auto" data-testid="support-tips">
            <h2 id="support-guide-title" className="font-nunito font-extrabold tracking-tight text-3xl text-[#17284b]">How to Support Your Learner</h2>
            <p className="font-nunito text-base text-[#596882] mt-2 mb-4">Simple, practical ways to get the most out of every workbook — at home, in class, or in therapy.</p>
            <Accordion type="single" collapsible className="w-full">
              {TIPS.map((t, i) => (
                <AccordionItem key={t.title} value={`tip-${i}`} className="border-[#d4dff0]">
                  <AccordionTrigger className="font-nunito font-bold text-base text-[#17284b] text-left hover:no-underline" data-testid={`support-tip-${i}`}>
                    <span className="flex items-center gap-3"><span className="text-[#ff8518] font-extrabold">{String(i + 1).padStart(2, "0")}</span>{t.title}</span>
                  </AccordionTrigger>
                  <AccordionContent className="font-nunito text-base text-[#596882] leading-relaxed pl-9">{t.body}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>
        <FreeTools />
        <CourseComingSoon />
        <NewsletterSection />
      </main>
      <SiteFooter />
    </div>
  );
}
